import {AlpineComponent} from "alpinejs";
import {default as AjaxList, ListOptions as AjaxListOptions, ListXDataContext as AjaxListXDataContext} from '../../../components/AjaxList/Classes/List';
import ListItem from "../../../components/AjaxList/Classes/Item";
import {IHistoryStateProps} from "../../../utils/historyState";

export type ListComponent = AlpineComponent<AjaxListXDataContext<ListItem> & {
  activeOrder: string;
  filterItems: (order: string) => void;
  filterOption: (type: string, event: Event) => void;
  filterSetOrder: (event: Event) => void;
  resetFilter: () => void;
}>;

export type ListOptions = AjaxListOptions & IHistoryStateProps;

const List = (options: ListOptions, filterData = {}):ListComponent => {
  return {
    ...AjaxList(options, filterData),
    activeOrder: '',

    // Filter - Order items by Title (ASC/DESC)
    filterSetOrder(event: Event) {
      const eTarget = event.currentTarget as HTMLSelectElement;
      const order = eTarget.selectedOptions[0].value;

      this.filteredItems.sort((a, b) => {
        if (order === 'ASC') {
          return a.title.localeCompare(b.title);
        } else {
          return b.title.localeCompare(a.title);
        }
      });
      this.filterItems(order);
    },

    // Filter options
    filterOption(type: string, event: Event) {
      const eTarget = event.target as HTMLInputElement;
      const value= eTarget.value.toString().toLowerCase();

      if (value.length >= 2) {
        // Filter results
        const filteredResults = this.filteredItems.filter(item =>
          item[type] && item[type].toString().toLowerCase().includes(value)
        );

        // Add results to ul list
        const ulParent = eTarget.parentElement;
        if (ulParent) {
          const list = ulParent.querySelector('ul') as HTMLUListElement;
          if (list) {
            list.innerHTML = ''; // Remove all existing li elements
            list.removeAttribute('hidden'); // Remove hidden attribute from ul

            const uniqueStates = new Set<string>();
            let isFirstItem = true;
            filteredResults.forEach(item => {
              const state = item[type];
              if (state && !uniqueStates.has(state)) {
                uniqueStates.add(state);

                const li = document.createElement('li');
                li.setAttribute('aria-selected', isFirstItem ? 'true' : 'false');
                isFirstItem = false;
                li.innerHTML = state.replace(new RegExp(value, 'gi'), (match: string) => `<mark>${match}</mark>`);
                li.addEventListener('click', () => {
                  eTarget.value = state;
                  list.setAttribute('hidden', 'hidden');
                  eTarget.focus();
                  this.filter!.data[type] = state; // Filter items
                });
                list.appendChild(li);
              }
            });
          }
        }

        // Show filter rest button
        const filterReset = document.querySelector('.button--filter') as HTMLElement;
        if (filterReset) {
          filterReset.classList.remove('d-none');
        }
      }

      // Remove filter data if input is empty
      if (value.length == 0) {
        delete this.filter!.data[type];
      }
    },

    filterItems($order = 'ASC') {
      if ($order == 'ASC') {
        this.filteredItems = this.items;
      }

      if (this.filter && this.filter.data) {
        this.filteredItems = this.filteredItems.filter((item) => {
          // for each filter property
          for (const property in this.filter!.data) {
            // Skip the order-by parameter
            if (property === 'order-by') {
              continue;
            }

            let filterPropertyValue = this.filter!.data[property];
            let itemPropertyValue = item[property];

            // if filter data property is empty, skip it
            if ((!filterPropertyValue) ||
              (Array.isArray(filterPropertyValue) && filterPropertyValue.length === 0)
            ) {
              continue;
            }

            // if item property has objects with uids, get flat list of uids
            if (Array.isArray(itemPropertyValue) && itemPropertyValue.length > 0 && itemPropertyValue[0].uid) {
              itemPropertyValue = itemPropertyValue.map((item: {uid: any}) => item.uid);
            }

            if (Array.isArray(filterPropertyValue) && Array.isArray(itemPropertyValue)) {
              // if filter data property and item property are both arrays, check if at least one item property is in filter data property
              if (!(itemPropertyValue as Array<any>).some((value) => (filterPropertyValue as Array<any>).includes(value))) {
                return false;
              }
            } else if (Array.isArray(filterPropertyValue)) {
              // if filter data property is an array and item property is not, check if item property is in filter data property
              if (!filterPropertyValue.includes(itemPropertyValue)) {
                return false;
              }
            } else if (Array.isArray(itemPropertyValue)) {
              // if item property is an array and filter data property is not, check if filter data property is in item property
              if (!(itemPropertyValue as Array<any>).includes(Number(filterPropertyValue))) {
                return false;
              }
            } else if (filterPropertyValue !== itemPropertyValue) {
              // if filter data property and item property are not arrays, check if they are equal
              return false;
            }
          }

          return true;
        });
      }

      if (this.pagination) {
        this.pagination.setTotalItems(this.filteredItems.length);
        this.pagination.goToPage(1);
      }
    },

    // Reset filter
    resetFilter() {
      this.filter!.data = {};

      // Hide filter rest button
      const filterReset = document.querySelector('.button--filter') as HTMLElement;
      if (filterReset) {
        filterReset.classList.add('d-none');
      }
    }
  }
}

export default List;
